
/**
 * Checks if two rectangles overlap.
 */
const doRectanglesOverlap = (r1, r2, spacing = 0) => {
    return !(
        r2.x > r1.x + r1.width + spacing ||
        r2.x + r2.width + spacing < r1.x ||
        r2.y > r1.y + r1.height + spacing ||
        r2.y + r2.height + spacing < r1.y
    );
};

/**
 * Finds a position for a new node that doesn't overlap with existing nodes.
 * Used a spiral search pattern to find the closest valid position to the preferred one.
 * 
 * @param {Array} nodes - Existing nodes on the canvas
 * @param {Object} preferredPosition - { x, y }
 * @param {number} width - Width of the new node
 * @param {number} height - Height of the new node
 * @param {number} spacing - Minimum gap between nodes
 * @returns {Object} - { x, y }
 */
export const findNonOverlappingPosition = (nodes, preferredPosition, width, height, spacing = 20) => {
    if (!nodes || nodes.length === 0) {
        return preferredPosition;
    }

    const { x: startX, y: startY } = preferredPosition;

    // Check if the preferred position is already clear
    const initialRect = { x: startX, y: startY, width, height };
    const hasInitialOverlap = nodes.some(node => {
        // Handle different node data structures (react-flow vs custom)
        const nodeX = node.position ? node.position.x : (node.position_x || 0);
        const nodeY = node.position ? node.position.y : (node.position_y || 0);
        // Fallback or use style width/height if available, otherwise assume standard size
        const nodeW = node.width || (node.style && node.style.width) || 200;
        const nodeH = node.height || (node.style && node.style.height) || 200;

        return doRectanglesOverlap(initialRect, { x: nodeX, y: nodeY, width: nodeW, height: nodeH }, spacing);
    });

    if (!hasInitialOverlap) {
        return preferredPosition;
    }

    // Spiral search parameters
    // We'll search in a grid pattern spiraling out
    // Grid step size roughly half the node size for granularity
    const stepX = width + spacing;
    const stepY = height + spacing;

    // Search up to 5 layers deep
    for (let layer = 1; layer <= 10; layer++) {
        // A layer is a square ring around the center
        // Top edge
        for (let i = -layer; i <= layer; i++) {
            const candidates = [
                // Top row
                { x: startX + i * stepX, y: startY - layer * stepY },
                // Bottom row
                { x: startX + i * stepX, y: startY + layer * stepY },
            ];

            // If we are not at the corners, add left and right edges
            if (i !== -layer && i !== layer) {
                // Left column
                candidates.push({ x: startX - layer * stepX, y: startY + i * stepY });
                // Right column
                candidates.push({ x: startX + layer * stepX, y: startY + i * stepY });
            }

            for (const pos of candidates) {
                const rect = { x: pos.x, y: pos.y, width, height };
                const overlaps = nodes.some(node => {
                    const nodeX = node.position ? node.position.x : (node.position_x || 0);
                    const nodeY = node.position ? node.position.y : (node.position_y || 0);
                    const nodeW = node.width || (node.style && node.style.width) || 200;
                    const nodeH = node.height || (node.style && node.style.height) || 200;

                    return doRectanglesOverlap(rect, { x: nodeX, y: nodeY, width: nodeW, height: nodeH }, spacing);
                });

                if (!overlaps) {
                    return pos;
                }
            }
        }
    }

    // If we couldn't find a spot (unlikely), return preferred with offset
    return { x: startX + 50, y: startY + 50 };
};
